import tippy, {Instance} from "tippy.js";
import type {MultipleTargets, Props} from "tippy.js";
import {$, $$, createElement, createSpinner} from "./Dom.ts";
import {$get} from "./Ajax.ts";
import {createFocusTrap} from "./FocusTrap.ts";

export const createSimpleTooltip = (target: Element, options: Partial<Props> = {}) => {
    return tippy(target, {
        allowHTML: true,
        theme: 'openscop',
        ...options
    })
}
export const createComplexTooltip = (selector: MultipleTargets, options: Partial<Props>) => {
    return tippy(selector, options)
}

export const createTooltip = (selector: MultipleTargets) => {
    return tippy(selector, {
        allowHTML: true,
        theme: 'openscop',
        content(reference) {
            const title = reference.getAttribute('title');
            if (title) {
                reference.removeAttribute('title');
                reference.setAttribute('aria-label', title)
                return title;
            }
            return ""
        },
    })
}

export const helperStopPropagation = {
    onTrigger: (_instance: Instance<Props>, event: Event) => {
        event.stopPropagation()
        event.stopImmediatePropagation()
        event.preventDefault()
    },
    onUntrigger(_instance: Instance<Props>, event: Event) {
        event.stopPropagation()
        event.stopImmediatePropagation()
        event.preventDefault()
    },
}

export const initTooltips = (container: Document | HTMLElement | string | null = document) => {
    if (container === null) return
    let parent: HTMLElement | Document | null
    if (typeof container === 'string') parent = $<HTMLElement>(container)
    else parent = container

    if (parent) {
        createTooltip($$<HTMLElement>('[title]:not([data-help])', parent))
        initIconTooltip(parent)
        initHelp(parent)
    }
}

function initIconTooltip(container: Document | HTMLElement) {
    createComplexTooltip($$('[data-tooltip=""]', container), {
        content: () => createSpinner("md", '#fff'),
        allowHTML: true,
        interactive: true,
        ...helperStopPropagation,
        theme: 'openscop',
        onCreate(instance) {
            // @ts-ignore
            instance._isLoaded = false;
        },
        onShow: getToolTipContent
    })
}

function initHelp(container: Document | HTMLElement) {
    const elements = $$('[data-help]', container)
    const instances = createComplexTooltip(elements, {
        content: () => createSpinner("md", '#fff'),
        allowHTML: true,
        hideOnClick: true,
        interactive: true,
        appendTo: document.body,
        theme: 'openscop',
        trigger: 'manual',
        onShow: getToolTipContent,
    })
    instances.forEach(instance => {
        instance.reference.addEventListener('click', (event) => {
            event.stopPropagation()
            event.stopImmediatePropagation()
            event.preventDefault()
            instance.show()
        })
        instance.reference.addEventListener('keypress', (event) => {
            if ((event as KeyboardEvent).key === 'Enter') {
                event.stopPropagation()
                event.stopImmediatePropagation()
                event.preventDefault()
                instance.show()
            }
        })
    })
}


function getToolTipContent(instance: Instance<Props>) {
    instance.popper.setAttribute('tabindex', "0")
    const closeTooltip = (event: Event) => {
        const isClick = event.type === "click"
        const isEnter = event.type === "keypress" && (event as KeyboardEvent).key === "Enter"
        if (isClick || isEnter) {
            instance.hide()
        }
    }
    const renderAideContextuelle = (content: string) => {
        // la réponse a un max-age=600 pour éviter les multiples requêtes
        // @ts-ignore
        instance._isLoaded = true;
        const parentdiv = createElement('div')
        const contentDiv = createElement('div')
        contentDiv.innerHTML = content
        const closeDiv = createElement('div', ['fr-grid-row', 'fr-grid-row--right'])
        const closeBtn = createElement('button', ['fr-btn', 'fr-btn--close', 'fr-mr-0-5v'])
        closeBtn.setAttribute('tabindex', '0')
        closeBtn.setAttribute('title', 'Fermer la fenêtre d\'information')
        closeBtn.setAttribute('aria-label', 'Fermer la fenêtre d\'information')
        closeBtn.innerHTML = `Fermer`

        closeBtn.addEventListener('click', closeTooltip)
        closeBtn.addEventListener('keypress', closeTooltip)
        closeDiv.appendChild(closeBtn)
        parentdiv.appendChild(closeDiv)
        parentdiv.appendChild(contentDiv)
        instance.setContent(parentdiv)
        const trap = createFocusTrap(instance.popper)
        element.setAttribute('aria-label', content)
        if (instance.popper.getBoundingClientRect().height > 700) {
            instance.popper.classList.add('tooltip-xl')
        }
        instance.setProps({
            onShown() {
                trap.activate()
            },
            onHide() {
                trap.deactivate()
            }
        })
    }
    const element = (instance.reference as HTMLElement)
    let context = element.dataset.help
    const position: any = element.dataset.position ?? null
    if(position) instance.setProps({placement: position})
    if (context) {
        $get(`${window.Openscop.ajax_help_url}ctx-help-${context}`)
            .then(({body}) => {
                renderAideContextuelle(body)
            })
            .catch((error: IAjaxError) => instance.setContent(`erreur : ${error.code}`))
    } else {
        const title = element.title
        if(title)  {
            element.title = ""
            element.ariaLabel = title
            renderAideContextuelle(title)
        } else {
            const titleLabel = element.ariaLabel
            if(titleLabel) renderAideContextuelle(titleLabel)
        }
    }
}